import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {compose} from 'redux';
import {withRouter} from 'react-router-dom';
import classNames from 'classnames';
import Button from '@spothero/ui/button';
import TextButton from '@spothero/ui/text-button';
import DOMUtils from '@spothero/utils/dom';
import FormatUtils from 'utils/format';
import {push} from 'router/router-actions';
import {routeToSpotDetails} from 'router/router-utils';
import withConfig from 'config/withConfig';

export const SPOTLISTITEM_PROP_TYPES = {
    history: PropTypes.shape({
        location: PropTypes.object,
    }),
    isInRecommendedSpotsModal: PropTypes.bool,
    isMobile: PropTypes.bool.isRequired,
    spot: PropTypes.object.isRequired,
    isActive: PropTypes.bool,
    showDetailsButton: PropTypes.bool,
    showBookButton: PropTypes.bool,
    isSoldOut: PropTypes.bool,
    isAdmin: PropTypes.bool,
    isRebookingReservation: PropTypes.bool,
    bypassOnBookSpotClick: PropTypes.bool,
    cancelAllSpotRequests: PropTypes.func,
    updateSelectedSpot: PropTypes.func,
    onBookSpotClick: PropTypes.func,
    pushTo: PropTypes.func,
    onShowDetailsClick: PropTypes.func,
    FacilitySummary: PropTypes.node,
};

export const SPOTLISTITEM_IMAGE_DIMENSIONS = isMobile => {
    const width = isMobile ? 75 : 96;

    return {
        width,
        height: width,
    };
};

export class SpotListItemBase extends Component {
    static propTypes = SPOTLISTITEM_PROP_TYPES;
    state = {
        hovered: false,
    };

    componentDidMount() {
        if (!this.props.isInRecommendedSpotsModal) {
            document.addEventListener('spotHover', this._onSpotHover);
        }
    }

    componentWillUnmount() {
        if (!this.props.isInRecommendedSpotsModal) {
            document.removeEventListener('spotHover', this._onSpotHover);
        }
    }

    _onSpotHover = evt => {
        const {
            detail: {hovered, spotId},
        } = evt;

        if (spotId === this.props.spot.spotId) {
            this.setState({
                hovered,
            });
        }
    };

    _onShowDetailsClick = evt => {
        evt.preventDefault();

        const {
            history: {location},
            spot: {spotId},
            pushTo,
            onShowDetailsClick,
        } = this.props;

        if (onShowDetailsClick) {
            onShowDetailsClick(spotId);
        }

        routeToSpotDetails({
            method: pushTo,
            location,
            spotId,
            historyState: {referrer: 'list'},
        });
    };

    _onMouseEnter = () => {
        DOMUtils.sendCustomEvent('spotHover', {
            hovered: true,
            spotId: this.props.spot.spotId,
        });
    };

    _onMouseLeave = () => {
        DOMUtils.sendCustomEvent('spotHover', {
            hovered: false,
            spotId: this.props.spot.spotId,
        });
    };

    _onBookSpotClick = evt => {
        evt.preventDefault();
        evt.stopPropagation();

        const {
            spot,
            cancelAllSpotRequests,
            onBookSpotClick,
            updateSelectedSpot,
        } = this.props;

        if (cancelAllSpotRequests) {
            cancelAllSpotRequests();
        }

        if (onBookSpotClick) {
            onBookSpotClick(spot);
        } else {
            updateSelectedSpot({
                spot,
                referrer: 'list',
                goToSpotUrl: true,
            });
        }
    };

    _renderOfferSchema = () => {
        const {
            spot: {
                selectedRate: {
                    duration,
                    display_price: displayPriceRaw,
                    starts,
                    ends,
                    fullUrl,
                    currency_type: currencyType,
                },
                title,
            },
        } = this.props;
        const {displayPrice} = FormatUtils.price(displayPriceRaw);
        const description = `Reserved Parking at ${title} for ${duration} Hour${
            duration === 1 ? '' : 's'
        }`;

        return (
            <>
                <meta itemProp="name" content="Parking" />
                <meta itemProp="url" content={fullUrl} />
                <meta itemProp="description" content={description} />
                <meta itemProp="priceCurrency" content={currencyType} />
                <meta itemProp="price" content={displayPrice} />
                <meta
                    itemProp="availability"
                    content="http://schema.org/InStock"
                />
                <meta itemProp="validFrom" content={starts} />
                <meta itemProp="validThrough" content={ends} />
                <span
                    itemProp="offeredBy"
                    itemScope
                    itemType="http://schema.org/Organization"
                >
                    <meta itemProp="name" content="SpotHero" />
                    <meta itemProp="url" content="https://spothero.com" />
                </span>
            </>
        );
    };

    render() {
        const {
            isInRecommendedSpotsModal,
            isMobile,
            spot,
            isActive,
            showDetailsButton,
            showBookButton,
            isSoldOut,
            isAdmin,
            isRebookingReservation,
            FacilitySummary,
            bypassOnBookSpotClick,
        } = this.props;
        const {selectedRate, spotId} = spot;
        const {hovered} = this.state;
        const {fullUrl, unavailable} = selectedRate;
        const containerClasses = classNames(
            'SpotListItem',
            `result${spotId}`,
            {'SpotListItem-active': isActive},
            {'SpotListItem-hovered': hovered},
            {'SpotListItem-sold-out': isSoldOut},
            {unavailable}
        );
        const bookItClasses = classNames('SpotListItem-book-it', {
            'admin-border-button': isAdmin && isRebookingReservation,
        });

        // bypassing the Book Now button's onClick allows the card to handle the click.
        const onBookClick = bypassOnBookSpotClick
            ? null
            : this._onBookSpotClick;

        return (
            <div
                className={containerClasses}
                data-spot={spotId}
                itemProp="offers"
                itemScope
                itemType="http://schema.org/Offer"
                onMouseEnter={
                    !isInRecommendedSpotsModal ? this._onMouseEnter : null
                }
                onMouseLeave={
                    !isInRecommendedSpotsModal ? this._onMouseLeave : null
                }
                onClick={
                    !isInRecommendedSpotsModal && !isSoldOut
                        ? this._onShowDetailsClick
                        : null
                }
            >
                {this._renderOfferSchema()}
                <div
                    itemProp="availableAtOrFrom"
                    itemScope
                    itemType="http://schema.org/ParkingFacility"
                >
                    {FacilitySummary}
                </div>
                <div className="controls">
                    {showDetailsButton && (
                        <TextButton className="show-details">
                            {isMobile ? 'View ' : ''}Details
                        </TextButton>
                    )}
                    {isAdmin && unavailable ? (
                        <Button color="primary" itemProp="url" disabled>
                            Unavailable <sup className="text-light">*</sup>
                        </Button>
                    ) : (
                        showBookButton &&
                        !isSoldOut && (
                            <TextButton
                                className={bookItClasses}
                                href={fullUrl}
                                color="primary"
                                itemProp="url"
                                onClick={onBookClick}
                            >
                                {isRebookingReservation
                                    ? 'Rebook It'
                                    : 'Book Now'}
                            </TextButton>
                        )
                    )}
                </div>
                {isSoldOut && (
                    <div className="SpotListItemRow-sold-out">
                        <p>Sold Out</p>
                    </div>
                )}
            </div>
        );
    }
}

const mapDispatchToProps = {
    pushTo: push,
};

const enhance = compose(
    withConfig(['isMobile']),
    withRouter,
    connect(null, mapDispatchToProps)
);

export default enhance(SpotListItemBase);
